import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaClock,
  FaCar,
  FaHourglassHalf,
  FaRoad,
  FaSearch,
  FaFilter,
  FaArrowLeft,
} from "react-icons/fa";
import {
  getPaymentMethodDisplay,
  getPaymentMethodBadgeClass,
} from "../utils/paymentDisplayUtils";
import { convertTo12HourFormat } from "../utils/timeUtils";
import socketService from "../utils/socketService";
import { getCorporateMember } from "../utils/corporateSession";

const Bookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const itemsPerPage = 10; // 10 bookings per page

  // Get user authentication data from Redux store instead of localStorage
  const { user } = useSelector((state) => state.auth);
  const corporateToken = localStorage.getItem("corporateToken");

  // Socket connection for real-time updates
  useEffect(() => {
    // User data is nested in user.data from Redux auth state
    const userData = user?.data || user;
    const userId = userData?._id || userData?.id;
    const token = user?.token || userData?.token;
    console.log('Bookings: Socket effect triggered', { userId, userData: !!userData, token: !!token });
    if (user && userId) {
      console.log('Bookings: Connecting socket...');
      const socket = socketService.connect();
      console.log('Bookings: Socket connect returned', { socket: !!socket, isConnected: socketService.isConnected });
      
      // Wait for socket connection before joining room
      const joinRoomInterval = setInterval(() => {
        const isConnected = socketService.socket?.connected || socketService.isConnected;
        console.log('Bookings: Checking connection status', { isConnected, hasSocket: !!socketService.socket });
        if (isConnected) {
          console.log('Bookings: Joining user room', userId);
          socketService.joinUserRoom(userId);
          
          // Also join corporate member room if this is a corporate staff member
          if (corporateToken) {
            const corporateMember = getCorporateMember();
            if (corporateMember && corporateMember.id) {
              console.log('Bookings: Joining corporate member room', corporateMember.id);
              socketService.joinCorporateMemberRoom(corporateMember.id);
            }
          }
          
          clearInterval(joinRoomInterval);
        }
      }, 100);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        console.log('Bookings: Room join timeout');
        clearInterval(joinRoomInterval);
      }, 5000);

      // Listen for driver assignment
      console.log('Bookings: Setting up driver assignment listener');
      socketService.listenForDriverAssignment((assignment) => {
        console.log('Bookings: Received driver assignment!', assignment);
        toast.success(`Driver ${assignment.driver?.name} assigned to your ride!`);
        // Refresh bookings list
        const { API_URL } = require("../config/apiConfig");
        const token = user?.token || userData?.token;
        const fetchBookings = async () => {
          try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.get(`${API_URL}/booking/my-bookings`, config);
            if (response.data?.success) {
              // Format and update bookings with new data
              const formattedBookings = response.data.data.map(formatBookingData);
              setBookings(formattedBookings);
              setTotalBookings(response.data.data?.length || 0);
              setTotalPages(Math.ceil((response.data.data?.length || 0) / itemsPerPage));
            }
          } catch (err) {
            console.error("Error refreshing bookings:", err);
          }
        };
        setTimeout(fetchBookings, 500);
      });
      console.log('Bookings: Driver assignment listener registered');

      // Helper function to format booking data
      const formatBookingData = (booking) => {
        const pickupLocation =
          typeof booking.pickupLocation === "object" && booking.pickupLocation?.name
            ? `${booking.pickupLocation.name}${booking.pickupLocation.state ? `, ${booking.pickupLocation.state}` : ""}`
            : booking.pickupAddress || booking.from || "";

        const dropLocation =
          typeof booking.dropLocation === "object" && booking.dropLocation?.name
            ? `${booking.dropLocation.name}${booking.dropLocation.state ? `, ${booking.dropLocation.state}` : ""}`
            : booking.dropAddress || booking.to || "";

        const cabName =
          typeof booking.cabType === "object" && booking.cabType?.name
            ? booking.cabType.name
            : "";

        let driverName = "Not assigned";
        let driverContact = "N/A";
        let driverInfo = null;

        if (booking.driver && booking.status === "assigned") {
          if (typeof booking.driver === "object" && booking.driver !== null) {
            driverName =
              `${booking.driver.firstName || ""} ${booking.driver.lastName || ""}`.trim() ||
              "Driver";
            driverContact = booking.driver.phone || "Not provided";
            driverInfo = booking.driver;
          } else if (typeof booking.driver === "string" && booking.driver) {
            driverName = "Driver Assigned";
            driverContact = "See details";
          }
        }

        const bookingId = booking.bookingId || booking._id;

        return {
          _id: booking._id,
          id: bookingId,
          bookingId: bookingId,
          from: pickupLocation,
          to: dropLocation,
          date: booking.pickupDate,
          time: booking.pickupTime || booking.travelTime || "Time not specified",
          duration: booking.duration || booking.travelTime || null,
          distance: booking.distance || null,
          status: booking.status,
          amount: booking.totalAmount,
          cabName: cabName,
          paymentMethod: booking.paymentMethod || "online",
          paymentStatus: booking.paymentStatus || "pending",
          driverName: driverName,
          driverContact: driverContact,
          driverInfo: driverInfo,
          rawData: booking
        };
      };

      // Listen for booking status changes
      socketService.onBookingStatusChanged((data) => {
        toast.info(`Booking status updated to: ${data.status}`);
        // Refresh bookings list
        const { API_URL } = require("../config/apiConfig");
        const token = user?.token || userData?.token;
        const fetchBookings = async () => {
          try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.get(`${API_URL}/booking/my-bookings`, config);
            if (response.data?.success) {
              // Update bookings with new data
              const formattedBookings = response.data.data.map(formatBookingData);
              setBookings(formattedBookings);
              setTotalBookings(response.data.data?.length || 0);
              setTotalPages(Math.ceil((response.data.data?.length || 0) / itemsPerPage));
            }
          } catch (err) {
            console.error("Error refreshing bookings:", err);
          }
        };
        setTimeout(fetchBookings, 500);
      });

      // Listen for driver removal
      socketService.onDriverRemoved((data) => {
        toast.info(`Driver removed from booking ${data.bookingId?.bookingId || data.bookingId}`);
        // Refresh bookings list
        const { API_URL } = require("../config/apiConfig");
        const fetchBookings = async () => {
          try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const response = await axios.get(`${API_URL}/booking/my-bookings`, config);
            if (response.data?.success) {
              // Format and update bookings with new data
              const formattedBookings = response.data.data.map(formatBookingData);
              setBookings(formattedBookings);
              setTotalBookings(response.data.data?.length || 0);
              setTotalPages(Math.ceil((response.data.data?.length || 0) / itemsPerPage));
            }
          } catch (err) {
            console.error("Error refreshing bookings:", err);
          }
        };
        setTimeout(fetchBookings, 500);
      });

      return () => {
        clearInterval(joinRoomInterval);
        // Don't disconnect socket - it's shared across pages
      };
    }
  }, [user]);

  useEffect(() => {
    // Import API URL from config
    const { API_URL } = require("../config/apiConfig");

    // Get bookings from MongoDB database only using API
    const fetchBookings = async () => {
      setIsLoading(true);

      try {
        // Get authentication token from Redux store
        let config = {};

        if (user && user.token) {
          config = {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          };
        } else if (corporateToken) {
          config = {
            headers: {
              Authorization: `Bearer ${corporateToken}`,
            },
          };
        }

        // Fetch user-specific bookings from MongoDB through API
        // Use the same endpoint as Dashboard for consistency
        const apiUrl = corporateToken && !user?.token ? `${API_URL}/corporate/bookings` : `${API_URL}/booking/my-bookings`;
        const response = await axios.get(apiUrl, config);

        if (response.data && response.data.success) {

          // Calculate pagination on frontend since my-bookings returns all user bookings
          const totalUserBookings = response.data.data.length;
          const calculatedTotalPages = Math.ceil(totalUserBookings / itemsPerPage);
          setTotalBookings(totalUserBookings);
          setTotalPages(calculatedTotalPages);
          setHasNextPage(currentPage < calculatedTotalPages);
          setHasPrevPage(currentPage > 1);

          // Transform API data to the format needed by the component
          const formattedBookings = response.data.data.map((booking) => {
            // Extract data depending on whether fields are populated objects or strings
            const pickupLocation =
              typeof booking.pickupLocation === "object" &&
              booking.pickupLocation?.name
                ? `${booking.pickupLocation.name}${booking.pickupLocation.state ? `, ${booking.pickupLocation.state}` : ""}`
                : booking.pickupAddress || booking.from || "";

            const dropLocation =
              typeof booking.dropLocation === "object" &&
              booking.dropLocation?.name
                ? `${booking.dropLocation.name}${booking.dropLocation.state ? `, ${booking.dropLocation.state}` : ""}`
                : booking.dropAddress || booking.to || "";

            const cabName =
              typeof booking.cabType === "object" && booking.cabType?.name
                ? booking.cabType.name
                : "";

            // Extract driver information if available
            let driverName = "Not assigned";
            let driverContact = "N/A";
            let driverInfo = null;

            if (booking.driver && booking.status === "assigned") {
              if (
                typeof booking.driver === "object" &&
                booking.driver !== null
              ) {
                driverName =
                  `${booking.driver.firstName || ""} ${booking.driver.lastName || ""}`.trim() ||
                  "Driver";
                driverContact = booking.driver.phone || "Not provided";
                driverInfo = booking.driver;
              } else if (typeof booking.driver === "string" && booking.driver) {
                driverName = "Driver Assigned";
                driverContact = "See details";
              }
            }

            // Format booking ID - use bookingId if available or _id as fallback
            const bookingId = booking.bookingId || booking._id;

            const isCorporateBooking =
              booking.createdByType === "corporate_staff" ||
              !!booking.corporateAccountId ||
              !!booking.corporateMemberId;

            // Normalize payment method - handle various backend values
            let normalizedPaymentMethod = "online"; // default to online
            let normalizedPaymentStatus = booking.paymentStatus || "pending";

            // Check actual payment method first (applies to all booking types)
            if (booking.paymentMethod) {
              const method = booking.paymentMethod.toLowerCase();
              if (
                method === "cod" ||
                method === "cash" ||
                method === "cash on delivery"
              ) {
                normalizedPaymentMethod = "cod";
              } else if (method === "wallet") {
                normalizedPaymentMethod = "wallet";
              } else if (method === "company" || method === "corporate" || method === "pay via company") {
                normalizedPaymentMethod = "company";
              } else {
                // Everything else is considered online payment
                normalizedPaymentMethod = "online";
                // For online payments, if status is not explicitly paid, set as paid
                if (
                  normalizedPaymentStatus !== "paid" &&
                  normalizedPaymentStatus !== "completed"
                ) {
                  normalizedPaymentStatus = "paid";
                }
              }
            } else if (isCorporateBooking) {
              // Only default to "company" if no explicit payment method is set AND it's a corporate booking
              normalizedPaymentMethod = "company";
            }


            return {
              id: booking._id,
              bookingId: bookingId,
              createdByType: booking.createdByType,
              corporateAccountId: booking.corporateAccountId,
              corporateMemberId: booking.corporateMemberId,
              from: pickupLocation,
              to: dropLocation,
              date: booking.pickupDate,
              time:
                booking.pickupTime ||
                booking.travelTime ||
                "Time not specified",
              duration: booking.duration || booking.travelTime || null,
              distance: booking.distance || null,
              status: booking.status,
              amount: booking.totalAmount,
              cabName: cabName,
              paymentMethod: normalizedPaymentMethod,
              paymentStatus: normalizedPaymentStatus,
              userName: booking.passengerDetails?.name,
              userEmail: booking.passengerDetails?.email,
              userPhone: booking.passengerDetails?.phone,
              driverName: driverName,
              driverContact: driverContact,
              driverInfo: driverInfo,
              assignedAt: booking.assignedAt,

              // Service type information (improved detection)
              serviceType: (() => {
                // First check explicit serviceType from backend - this is most reliable
                if (booking.serviceType && booking.serviceType !== "undefined") {
                  return booking.serviceType;
                }

                // Strong indicators for airport service
                if (booking.airport || booking.airportCode || booking.transferDirection) {
                  return "airport";
                }

                // Strong indicators for rental service
                if (booking.packageName || booking.rentalHours || booking.rentalKm) {
                  return "rental";
                }

                // Extract and clean location strings for comparison
                const extractLocationName = (location) => {
                  if (!location) return "";
                  if (typeof location === "object" && location?.name) {
                    return location.name.toLowerCase().trim();
                  }
                  if (typeof location === "string") {
                    // Skip ObjectIds (24 character hex strings)
                    if (location.length === 24 && /^[0-9a-fA-F]{24}$/.test(location)) {
                      return "";
                    }
                    return location.toLowerCase().trim();
                  }
                  return "";
                };

                const pickupName = extractLocationName(booking.pickupLocation) ||
                                 extractLocationName(booking.from) ||
                                 (booking.pickupAddress ? booking.pickupAddress.toLowerCase().trim() : "");
                const dropName = extractLocationName(booking.dropLocation) ||
                               extractLocationName(booking.to) ||
                               (booking.dropAddress ? booking.dropAddress.toLowerCase().trim() : "");

                // Only consider it rental if both locations are exactly the same AND non-empty
                // AND there are clear rental indicators
                if (pickupName && dropName && pickupName === dropName &&
                    (booking.packageName || booking.rentalHours || booking.rentalKm)) {
                  return "rental";
                }

                // For cases like "Porbandar to Dwarka" - these are clearly different cities = Outstation
                if (pickupName && dropName && pickupName !== dropName) {
                  return "outstation";
                }

                // If we can't determine locations clearly, default to outstation
                return "outstation";
              })(),

              // Trip type information (One Way vs Round Trip vs Multi-City)
              tripType: (() => {
                // Check if booking has explicit journey type (primary field)
                if (booking.journeyType && booking.journeyType !== "undefined") {
                  if (booking.journeyType === "roundTrip") {
                    return "roundTrip";
                  }
                  if (booking.journeyType === "oneWay") {
                    return "oneWay";
                  }
                  // Support legacy formats if any exist
                  if (booking.journeyType === "round_trip" || booking.journeyType === "round-trip") {
                    return "roundTrip";
                  }
                  if (booking.journeyType === "one_way" || booking.journeyType === "one-way") {
                    return "oneWay";
                  }
                }

                // Check if booking has explicit trip type (fallback)
                if (booking.tripType && booking.tripType !== "undefined") {
                  return booking.tripType;
                }

                // Check for round trip indicators
                if (booking.returnDate || booking.returnTime) {
                  return "roundTrip";
                }

                // Check for return date as string (not null/empty)
                if (booking.returnDate && booking.returnDate !== "null" && booking.returnDate !== "") {
                  return "roundTrip";
                }

                // Check for via cities (multi-city trip)
                if (booking.viaCities && booking.viaCities.length > 0) {
                  return "multiCity";
                }

                // Default to one way
                return "oneWay";
              })(),

              // Via cities information - check both database and potential URL params
              viaCities: (() => {
                // First check if booking has via cities
                if (booking.viaCities && Array.isArray(booking.viaCities) && booking.viaCities.length > 0) {
                  return booking.viaCities;
                }

                // Check if there's via cities data in any route parameters stored in the booking
                if (booking.routeData && booking.routeData.viaCities) {
                  return booking.routeData.viaCities;
                }

                // Check if via cities are stored as a string and need to be parsed
                if (booking.viaCitiesString) {
                  return booking.viaCitiesString.split(',').map(city => city.trim()).filter(city => city.length > 0);
                }

                return [];
              })(),

              // Rental service information
              packageName: booking.packageName,
              rentalHours: booking.rentalHours,
              rentalKm: booking.rentalKm,
              selectedAddOns: booking.selectedAddOns || [],
              addOnsCost: booking.addOnsCost || 0,

              rawData: booking, // Keep the original data for debugging
            };
          });

          setBookings(formattedBookings);
        } else {
          console.warn("No bookings found in the database");
          setBookings([]);
          setTotalBookings(0);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching bookings from API:", error);
        alert(
          "Failed to load bookings from the database. Please try again later.",
        );
        setBookings([]);
        setTotalBookings(0);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, []); // Fetch all user bookings once

  // Note: Filtering is now applied to current page data in filteredCurrentBookings above

  // Apply search and filter to all bookings, then paginate
  const normalizedSearch = searchTerm.trim().toLowerCase().replace(/^#/, "");
  const filteredBookings = bookings.filter((booking) => {
    const bookingIdStr = String(booking.bookingId || "").toLowerCase();
    const internalIdStr = String(booking.id || "").toLowerCase();

    const matchesSearch =
      (booking.from && booking.from.toLowerCase().includes(normalizedSearch)) ||
      (booking.to && booking.to.toLowerCase().includes(normalizedSearch)) ||
      (bookingIdStr && bookingIdStr.includes(normalizedSearch)) ||
      (internalIdStr && internalIdStr.includes(normalizedSearch)) ||
      (booking.cabName && booking.cabName.toLowerCase().includes(normalizedSearch));

    if (filter === "all") return matchesSearch;
    if (filter === "upcoming") {
      const bookingDate = new Date(booking.date);
      const today = new Date();
      return matchesSearch && bookingDate >= today;
    }
    if (filter === "completed") {
      const bookingDate = new Date(booking.date);
      const today = new Date();
      return matchesSearch && bookingDate < today;
    }

    return matchesSearch;
  });

  // Client-side pagination
  const filteredTotalPages = Math.max(1, Math.ceil(filteredBookings.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = filteredBookings.slice(startIndex, endIndex);

  // Reset to first page when filter or search changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, filter]);

  // Pagination handlers (based on filtered results)
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, filteredTotalPages));
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (isLoading) {
    return (
      <div className="bg-white min-h-screen flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* MOBILE HEADER (MakeMyTrip Style) - FIXED to viewport top */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-900 to-indigo-900 text-white shadow-md">
        <div className="flex items-center px-4 py-3 gap-2">
          <button 
            type="button"
            onClick={() => navigate(-1)} 
            className="text-white hover:text-gray-200 transition-colors p-1 flex-shrink-0"
            aria-label="Go Back"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="font-bold !text-sm leading-tight truncate">
              My Bookings
            </p>
          </div>
          <Link
            to="/"
            className="bg-white text-blue-600 px-3 py-1 rounded-md text-xs font-semibold hover:bg-gray-100 flex-shrink-0"
          >
            New Cab
          </Link>
        </div>
      </div>

      {/* Spacer to prevent header overlay on mobile */}
      <div className="md:hidden h-[52px]"></div>

      {/* TITLE SECTION (Desktop Only) */}
      <div className="hidden md:block mt-12">
        <div className="max-w-[1180px] mx-auto px-4">
          <div className="bg-blue-600 px-6 py-3 flex justify-between items-center rounded-md">
            <h1 className="text-white text-lg font-semibold">
              My Booking
            </h1>
            <Link
              to="/"
              className="bg-white text-blue-600 px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-edit"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" /><path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415" /><path d="M16 5l3 3" /></svg> Book a new cab
            </Link>
          </div>
        </div>
      </div>

      {/* SEARCH BAR & FILTER */}
      <div className="flex flex-col md:flex-row gap-3 px-4 py-4 max-w-[1180px] mx-auto mt-2">
        {/* SEARCH BOX */}
        <div className="flex items-center gap-2 bg-[#EAF0FC] px-6 py-2 rounded-md flex-1">
          {/* Search Icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 text-gray-600"
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="m15.7 14.3l-4.2-4.2c-.2-.2-.5-.3-.8-.3c.8-1 1.3-2.4 1.3-3.8c0-3.3-2.7-6-6-6S0 2.7 0 6s2.7 6 6 6c1.4 0 2.8-.5 3.8-1.4c0 .3 0 .6.3.8l4.2 4.2c.2.2.5.3.7.3s.5-.1.7-.3c.4-.3.4-.9 0-1.3zM6 10.5c-2.5 0-4.5-2-4.5-4.5s2-4.5 4.5-4.5s4.5 2 4.5 4.5s-2 4.5-4.5 4.5z"/>
          </svg>
          <input
            type="text"
            placeholder="Search Bookings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-transparent outline-none text-sm w-full"
          />
        </div>

        {/* FILTER SELECT */}
        <div className="flex items-center gap-2 bg-[#EAF0FC] px-3 py-2 rounded-md justify-between md:justify-start w-full md:w-auto">
          <div className="flex items-center gap-2">
            <FaFilter className="w-4 h-4 text-gray-700" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-transparent text-sm font-medium outline-none cursor-pointer w-full"
            >
              <option value="all">All Bookings</option>
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* BOOKING LIST */}
      <div className="max-w-[1180px] mx-auto space-y-4 px-4 pb-10">
        {currentBookings.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              No Bookings Found
            </h2>
            <p className="text-gray-600 mb-6">
              You haven't made any bookings yet or no bookings match your search.
            </p>
            <Link
              to="/"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 inline-block"
            >
              Book Your First Cab
            </Link>
          </div>
        ) : (
          <>
            {currentBookings.map((booking) => (
              <div key={booking._id || booking.bookingId || booking.id} className="bg-white rounded-xl shadow border overflow-hidden">
                {/* TOP SECTION */}
                <div className="relative p-4 flex items-center">
                  <div className="flex flex-col items-center mr-3">
                    <img
                      src="icons/route-icon.png"
                      alt="route"
                      className="w-6 h-16 object-contain"
                    />
                  </div>
                  {/* LEFT ROUTE */}
                  <div className="space-y-2 flex-1">
                    <div>
                      <p className="font-bold text-gray-900 text-lg">{booking.from}</p>
                      {booking.rawData?.pickupAddress && booking.rawData?.pickupAddress.trim().toLowerCase() !== booking.from?.trim().toLowerCase() && (
                        <p className="text-sm text-gray-500">
                          {booking.rawData?.pickupAddress}
                        </p>
                      )}
                    </div>

                    {booking.viaCities && booking.viaCities.length > 0 && (
                      <div className="pl-4 border-l-2 border-dashed border-gray-300 py-1 space-y-1">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          Via Cities:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {booking.viaCities.map((city, idx) => (
                            <span
                              key={idx}
                              className="bg-yellow-50 text-yellow-800 text-[10px] font-bold px-2 py-0.5 rounded border border-yellow-200 inline-flex items-center gap-1"
                            >
                              📍 {typeof city === "string" ? city : (city?.name || city?.address || "")}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="font-bold text-gray-900 text-lg">{booking.to}</p>
                      {booking.rawData?.dropAddress && booking.rawData?.dropAddress.trim().toLowerCase() !== booking.to?.trim().toLowerCase() && (
                        <p className="text-sm text-gray-500">
                          {booking.rawData?.dropAddress}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Add-on Services (Optional) */}
                  {booking.selectedAddOns && booking.selectedAddOns.length > 0 && (
                    <div className="mt-2.5 pt-2 border-t border-gray-100 flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-flex items-center gap-1">
                        ✨ Add-ons:
                      </span>
                      {booking.selectedAddOns.map((addon, aIdx) => (
                        <span key={aIdx} className="text-[11px] font-medium text-gray-700 bg-gray-50 px-2 py-0.5 rounded border border-gray-200">
                          {addon.name} (+₹{addon.price})
                        </span>
                      ))}
                    </div>
                  )}

                  {/* CENTER BRAND - TRULY CENTERED */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 hidden sm:flex items-center justify-center"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 900,
                      fontSize: "52.54px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      opacity: 0.5,
                    }}
                  >
                    <span className="text-blue-400">Cab</span>
                    <span className="text-orange-400">Zigo</span>
                  </div>

                  {/* RIGHT PRICE */}
                  <div className="text-right flex-shrink-0 ml-auto -mt-2">
                    <Link
                      to={`/booking/${booking.bookingId || booking.id}`}
                      className="text-sm text-blue-600 cursor-pointer block mb-2 font-medium"
                    >
                      👁 View Details
                    </Link>
                    <p className="text-3xl font-bold text-gray-900">
                      ₹{booking.amount || 0}
                    </p>
                  </div>
                </div>

                {/* BOTTOM DETAILS */}
                <div className="border-t grid grid-cols-2 gap-0 sm:grid-cols-3 md:flex md:flex-wrap items-stretch bg-gray-50 text-sm md:text-base text-gray-800 font-semibold overflow-hidden">
                  <div className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white flex-1 min-w-[120px] w-full border-r border-b border-gray-200">
                    <FaCalendarAlt className="text-gray-900 text-sm md:text-base" />
                    <span style={{ color: "#EA5833" }} className="font-semibold">
                      {booking.date
                        ? new Date(booking.date).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "Not specified"}
                    </span>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white flex-1 min-w-[100px] w-full border-r border-b border-gray-200">
                    <FaClock className="text-gray-900 text-sm md:text-base" />
                    <span style={{ color: '#EA5833' }} className="font-semibold">{convertTo12HourFormat(booking.time) || "Time not specified"}</span>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white flex-1 min-w-[120px] w-full border-r border-b border-gray-200">
                    <FaCar className="text-gray-900 text-sm md:text-base" />
                    <span className="font-semibold">{booking.cabName || "Not specified"}</span>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white flex-1 min-w-[120px] w-full border-r border-b border-gray-200">
                    <FaHourglassHalf className="text-gray-900 text-sm md:text-base" />
                    <span className="font-semibold">
                      {(() => {
                        if (booking.duration) {
                          if (booking.duration > 3600) {
                            const hours = Math.floor(booking.duration / 3600);
                            const minutes = Math.floor((booking.duration % 3600) / 60);
                            return `${hours}h ${minutes}m`;
                          }
                          if (typeof booking.duration === 'string') {
                            return booking.duration;
                          }
                          if (booking.duration > 60) {
                            const hours = Math.floor(booking.duration / 60);
                            const minutes = booking.duration % 60;
                            return `${hours}h ${minutes}m`;
                          }
                          return `${booking.duration}m`;
                        }
                        return "Not specified";
                      })()}
                    </span>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white flex-1 min-w-[120px] w-full border-r border-b border-gray-200">
                    <FaRoad className="text-gray-900 text-sm md:text-base" />
                    <span className="font-semibold">{booking.distance ? `${booking.distance} km` : "Not specified"}</span>
                  </div>

                  {/* PAYMENT */}
                  <div className="flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-600 font-bold flex-1 min-w-[100px] text-sm md:text-base w-full border-r border-b border-gray-200">
                    {getPaymentMethodDisplay(booking)}
                  </div>

                  {/* STATUS - right after Payment */}
                  <div className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1 font-bold flex-1 min-w-[140px] border-r border-b border-gray-200 ${
                    booking.status === "completed"
                      ? "bg-green-50 text-green-600"
                      : booking.status === "inProgress"
                      ? "bg-indigo-50 text-indigo-600"
                      : booking.status === "assigned"
                      ? "bg-amber-50 text-amber-700"
                      : booking.status === "cancelled"
                      ? "bg-red-50 text-red-600"
                      : "bg-orange-50 text-orange-600"
                  }`}>
                    <span className="whitespace-nowrap text-sm font-bold">
                      {booking.status === "inProgress" ? "In Progress"
                        : booking.status === "assigned" ? "Assigned"
                        : booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </span>
                    {/* Track Live button inside STATUS cell for inProgress */}
                    {booking.status === "inProgress" && (booking.bookingId || booking.id) && (
                      <a
                        href={`/track/${booking.bookingId || booking.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Track your driver live"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
                          color: '#fff', padding: '2px 10px', borderRadius: 99,
                          fontSize: 10, fontWeight: 700, textDecoration: 'none',
                          boxShadow: '0 0 6px rgba(99,102,241,0.5)', whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: '#10b981', display: 'inline-block',
                          animation: 'pulse 1.5s infinite',
                        }} />
                        Track Live
                      </a>
                    )}
                  </div>

                  {/* ACTIVE ACTIONS / TRACKING (Driver Coming - assigned only) */}
                  {booking.status === "assigned" && (booking.bookingId || booking.id) && (
                    <div className="flex items-center justify-center px-3 py-2 bg-white flex-1 min-w-[130px] w-full border-r border-b border-gray-200">
                      <a
                        href={`/track/${booking.bookingId || booking.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="See driver coming to you"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: 'linear-gradient(135deg,#f59e0b,#d97706)',
                          color: '#fff', padding: '4px 12px', borderRadius: 99,
                          fontSize: 11, fontWeight: 700, textDecoration: 'none',
                          boxShadow: '0 0 8px rgba(245,158,11,0.5)', whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: '#fff', display: 'inline-block',
                          animation: 'pulse 1.5s infinite',
                        }} />
                        Driver Coming
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Pagination Controls */}
        {(() => {
          const filteredTotalPages = Math.ceil(filteredBookings.length / itemsPerPage);
          return filteredTotalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <nav className="flex items-center space-x-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded border ${
                    currentPage === 1
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(filteredTotalPages, 10) }, (_, i) => {
                  let pageNumber;
                  if (filteredTotalPages <= 10) {
                    pageNumber = i + 1;
                  } else {
                    const start = Math.max(1, currentPage - 4);
                    const end = Math.min(filteredTotalPages, start + 9);
                    pageNumber = start + i;
                    if (pageNumber > end) return null;
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageClick(pageNumber)}
                      className={`px-3 py-1 rounded ${
                        currentPage === pageNumber
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-blue-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                }).filter(Boolean)}

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === filteredTotalPages}
                  className={`px-3 py-1 rounded border ${
                    currentPage === filteredTotalPages
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Bookings;
